import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, TextField, List, ListSubheader, ListItemButton,
  ListItemText, ListItemIcon, CircularProgress, Box, Typography, InputAdornment,
} from '@mui/material';
import { Search as SearchIcon, School, Person, ClassOutlined as ClassIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { principalAPI } from '@/services/api';

const ENTITY = {
  students: { label: 'Students', icon: School, href: () => `/principal/students` },
  teachers: { label: 'Teachers', icon: Person, href: () => `/principal/teachers` },
  classes: { label: 'Classes', icon: ClassIcon, href: () => `/principal/classes` },
};

const GlobalSearch = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const runSearch = useCallback((term) => {
    if (term.trim().length < 3) {
      setResults(null);
      setLoading(false);
      return;
    }
    principalAPI.globalSearch(term.trim())
      .then((res) => setResults(res.data.results || { students: [], teachers: [], classes: [] }))
      .catch(() => setResults({ students: [], teachers: [], classes: [] }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => runSearch(q), 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, runSearch]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQ(''); setResults(null); }
  }, [open]);

  const go = (entityKey, row) => {
    navigate(`${ENTITY[entityKey].href()}?highlight=${row.id}`);
    onClose();
  };

  const isEmpty = results && Object.values(results).every(arr => !arr.length);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm"
      PaperProps={{ sx: { position: 'fixed', top: 80, m: 0, borderRadius: 3 } }}>
      <Box sx={{ p: 2 }}>
        <TextField
          inputRef={inputRef} fullWidth autoFocus placeholder="Search students, teachers, classes…"
          value={q} onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
        />
      </Box>
      <Box sx={{ maxHeight: 360, overflowY: 'auto', pb: 1 }}>
        {loading && <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>}
        {!loading && q.length > 0 && q.length < 3 && (
          <Typography variant="body2" sx={{ px: 3, py: 2, color: 'text.secondary' }}>Type at least 3 characters…</Typography>
        )}
        {!loading && isEmpty && (
          <Typography variant="body2" sx={{ px: 3, py: 2, color: 'text.secondary' }}>No matches for "{q}".</Typography>
        )}
        {!loading && results && Object.entries(results).map(([key, rows]) =>
          rows.length ? (
            <List key={key} dense subheader={<ListSubheader>{ENTITY[key].label}</ListSubheader>}>
              {rows.map(row => {
                const Icon = ENTITY[key].icon;
                return (
                  <ListItemButton key={row.id} onClick={() => go(key, row)}>
                    <ListItemIcon sx={{ minWidth: 36 }}><Icon fontSize="small" /></ListItemIcon>
                    <ListItemText primary={row.name} secondary={row.subtitle} />
                  </ListItemButton>
                );
              })}
            </List>
          ) : null
        )}
      </Box>
    </Dialog>
  );
};

export default GlobalSearch;
